import React, { useState, useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import API from '../api/axios';
import { toast } from 'react-toastify';
import Layout from '../components/Layout';
import ProjectSearchSelect from '../components/ProjectSearchSelect';
import PageLoader from '../components/PageLoader';
import {
  FiPlus, FiSearch, FiFilter, FiEdit2, FiTrash2, FiX, FiChevronDown,
  FiSend, FiDownload, FiMoreHorizontal, FiCheck, FiFileText, FiPrinter,
} from 'react-icons/fi';
import './QuotationMgmt.css';

const PAGE_SIZE = 8;

// Converts a Material Plan category's materials into BOQ items for a quotation section.
function catToItems(cat) {
  return (cat.materials || []).map(m => ({
    description: m.materialName,
    heightFt: m.heightFt || '',
    widthFt: m.widthFt || '',
    unit: m.unit || 'Nos',
    qty: m.requiredQty || 1,
    rate: m.rate || 0,
  }));
}

const STATUS_CFG = {
  draft:    { label: 'Draft',    color: '#64748b', bg: '#f1f5f9', border: '#e2e8f0' },
  sent:     { label: 'Sent',     color: '#2563eb', bg: '#eff6ff', border: '#bfdbfe' },
  approved: { label: 'Approved', color: '#16a34a', bg: '#f0fdf4', border: '#bbf7d0' },
  rejected: { label: 'Rejected', color: '#dc2626', bg: '#fef2f2', border: '#fecaca' },
};


function StatusBadge({ status }) {
  const c = STATUS_CFG[status] || STATUS_CFG.draft;
  return <span className="quo-badge" style={{ color: c.color, background: c.bg, border: `1px solid ${c.border}` }}>{c.label}</span>;
}
function fmt(iso) { if (!iso) return '—'; return new Date(iso).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' }); }
function fmtINR(n) { if (!n && n !== 0) return '—'; return '₹' + Number(n).toLocaleString('en-IN'); }

function freshItem() {
  return { description: '', heightFt: '', widthFt: '', unit: 'Nos', qty: '', rate: '' };
}
function freshSections() {
  return [{ title: '', items: [freshItem()] }];
}

function ConfirmDialog({ title, message, confirmLabel = 'Confirm', confirmColor = '#2563eb', icon, onConfirm, onCancel }) {
  return (
    <div className="quo-confirm-overlay" onClick={onCancel}>
      <div className="quo-confirm-box" onClick={e => e.stopPropagation()}>
        {icon && <div className="quo-confirm-icon">{icon}</div>}
        <h4 className="quo-confirm-title">{title}</h4>
        <p className="quo-confirm-msg">{message}</p>
        <div className="quo-confirm-actions">
          <button className="quo-confirm-cancel" onClick={onCancel}>Cancel</button>
          <button className="quo-confirm-ok" style={{ background: confirmColor }} onClick={onConfirm}>{confirmLabel}</button>
        </div>
      </div>
    </div>
  );
}

export default function QuotationMgmt() {
  const location = useLocation();
  const [quotations, setQuotations]   = useState([]);
  const [selected, setSelected]       = useState(null);
  const [search, setSearch]           = useState('');
  const [filterStatus, setFilterStatus] = useState('');
  const [showFilter, setShowFilter]   = useState(false);
  const [page, setPage]               = useState(1);
  const [showModal, setShowModal]     = useState(false);
  const [editQuotation, setEditQuotation] = useState(null);
  const [moreMenuId, setMoreMenuId]   = useState(null);
  const [clients, setClients]         = useState([]);
  const [projects, setProjects]       = useState([]);
  const [editingItem, setEditingItem] = useState(null);
  const [editingItemData, setEditingItemData] = useState({});
  const [mergeSelection, setMergeSelection] = useState([]); // [{si, ii}, ...] within one section
  const [confirm, setConfirm]         = useState(null);
  const [showSendChoice, setShowSendChoice] = useState(false);
  const [sending, setSending] = useState(false);
  const [loading, setLoading]         = useState(true);
  const [prefilledClient, setPrefilledClient] = useState(null);
  const [prefilledProject, setPrefilledProject] = useState(null);
  const [showRejectModal, setShowRejectModal] = useState(false);
  const [rejectTarget, setRejectTarget]       = useState(null);
  const [rejectReason, setRejectReason]       = useState('');
  const [rejectNotify, setRejectNotify]       = useState(false);
  const [rejectClientEmail, setRejectClientEmail] = useState('');

  function askConfirm(opts) {
    return new Promise(resolve => {
      setConfirm({
        ...opts,
        onConfirm: () => { setConfirm(null); resolve(true); },
        onCancel:  () => { setConfirm(null); resolve(false); },
      });
    });
  }

  useEffect(() => {
    load();
    API.get('/clients').then(r => setClients(r.data)).catch(() => {});
    Promise.all([
      API.get('/projects'),
      API.get('/material-planning').catch(() => ({ data: [] })),
    ]).then(([projRes, planRes]) => {
      const plannedIds = new Set((planRes.data || []).map(id => String(id)));
      setProjects(projRes.data.filter(p => plannedIds.has(String(p._id))));
    }).catch(() => {});
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Auto-open new quotation modal if navigated from Customer or Project page
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const clientId    = params.get('clientId');
    const clientName  = params.get('clientName');
    const projectId   = params.get('projectId');
    const viewId      = params.get('viewId');

    // viewId: navigate directly to view an existing quotation
    if (viewId) {
      API.get(`/quotations/${viewId}`).then(r => {
        setSelected(r.data);
      }).catch(() => {});
      return;
    }

    if (clientId || projectId) {
      if (clientId && clientName) {
        setPrefilledClient({ _id: clientId, name: decodeURIComponent(clientName) });
      }
      if (projectId) {
        API.get(`/projects/${projectId}`).then(r => {
          const p = r.data;
          setPrefilledProject({ _id: p._id, title: p.title });
          if (!clientId && p.client) {
            setPrefilledClient({ _id: p.client._id || p.client, name: p.client.name || '' });
          }
        }).catch(() => {});
      }
      setEditQuotation(null);
      setShowModal(true);
    }
  }, [location.search]);

  function load() {
    API.get('/quotations')
      .then(r => {
        setQuotations(r.data);
        setSelected(prev => {
          if (prev) return r.data.find(q => q._id === prev._id) || r.data[0] || null;
          return r.data[0] || null;
        });
      })
      .catch(() => setQuotations([]))
      .finally(() => setLoading(false));
  }

  // filter + search
  const filtered = quotations.filter(q => {
    if (filterStatus && q.status !== filterStatus) return false;
    if (!search) return true;
    const s = search.toLowerCase();
    return (q.quotationNumber || '').toLowerCase().includes(s)
      || (q.clientName || q.client?.name || '').toLowerCase().includes(s)
      || (q.projectName || q.project?.title || '').toLowerCase().includes(s);
  });

  const totalPages = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE));
  const paginated  = filtered.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE);

  async function selectQuotation(q) {
    setSelected(q); // show immediately with list data
    setEditingItem(null);
    setMoreMenuId(null);
    // fetch full detail to get client.email and other populated fields
    try {
      const r = await API.get(`/quotations/${q._id}`);
      setSelected(r.data);
    } catch { /* keep list data if fetch fails */ }
  }

  async function del(id) {
    const ok = await askConfirm({
      title: 'Delete Quotation',
      message: 'This action cannot be undone. Are you sure you want to delete this quotation?',
      confirmLabel: 'Delete', confirmColor: '#dc2626', icon: '🗑️',
    });
    if (!ok) return;
    try {
      await API.delete(`/quotations/${id}`);
      const next = quotations.filter(q => q._id !== id);
      setQuotations(next);
      setSelected(next[0] || null);
      toast.success('Quotation deleted');
    } catch { toast.error('Delete failed'); }
    setMoreMenuId(null);
  }

  async function updateStatus(id, status, rejectionReason, notify) {
    try {
      const r = await API.put(`/quotations/${id}`, { status, ...(rejectionReason ? { rejectionReason } : {}), ...(notify ? { notifyRejection: true } : {}) });
      setQuotations(prev => prev.map(q => q._id === id ? r.data : q));
      if (selected?._id === id) setSelected(r.data);
      toast.success(`Marked as ${STATUS_CFG[status]?.label}`);
    } catch { toast.error('Failed to update status'); }
    setMoreMenuId(null);
  }

  async function confirmAndUpdateStatus(id, status) {
    const label = STATUS_CFG[status]?.label || status;
    const ok = await askConfirm({
      title: `Change status to "${label}"?`,
      message: `This will update the quotation status to ${label}. This action cannot be undone.`,
      confirmLabel: `Yes, mark as ${label}`,
      confirmColor: status === 'approved' ? '#16a34a' : '#2563eb',
      icon: status === 'approved' ? '✅' : '📤',
    });
    if (ok) await updateStatus(id, status);
  }

  function handleRejectClick(id) {
    const quo = quotations.find(q => q._id === id) || selected;
    const email = quo?.client?.email || '';
    setRejectTarget(id);
    setRejectReason('');
    setRejectClientEmail(email);
    setRejectNotify(!!email);
    setShowRejectModal(true);
    setMoreMenuId(null);
  }

  async function submitRejection() {
    if (!rejectReason.trim()) return toast.error('Please enter a rejection reason');
    setShowRejectModal(false);
    await updateStatus(rejectTarget, 'rejected', rejectReason.trim(), rejectNotify);
    setRejectTarget(null);
    setRejectReason('');
    setRejectClientEmail('');
    setRejectNotify(false);
  }

  function beginSendQuotation() {
    if (!selected) return;
    setShowSendChoice(true);
  }

  async function sendQuotation(level) {
    if (!selected) return;
    const clientEmail = selected.client?.email;
    const ok = await askConfirm({
      title: 'Send Quotation',
      message: clientEmail
        ? `Send ${selected.quotationNumber} to ${selected.clientName || 'customer'}? A PDF (${level}-wise) will be attached and emailed to ${clientEmail}.`
        : `Mark ${selected.quotationNumber} as Sent? No customer email on file — status will update but no email will be sent.`,
      confirmLabel: 'Send', confirmColor: '#2563eb', icon: '📤',
    });
    if (!ok) return;
    setSending(true);
    try {
      const r = await API.post(`/quotations/${selected._id}/send`, { level });
      setQuotations(prev => prev.map(q => q._id === selected._id ? r.data.quotation : q));
      setSelected(r.data.quotation);
      if (r.data.emailSent) {
        toast.success(`Quotation sent! Email delivered to ${r.data.emailTo}`);
      } else if (r.data.emailTo) {
        toast.warning('Status updated but email delivery failed. Check mail logs.');
      } else {
        toast.success('Marked as Sent (no customer email on file)');
      }
    } catch { toast.error('Failed to send quotation'); }
    finally { setSending(false); }
  }

  function sendWhatsApp() {
    if (!selected) return;
    const phoneDigits = (selected.client?.phone || '').replace(/\D/g, '');
    if (!phoneDigits) {
      toast.error('No phone number on file for this customer');
      return;
    }
    // Assume 10-digit Indian mobile numbers; keep as-is if a country code is already present
    const phone = phoneDigits.length === 10 ? `91${phoneDigits}` : phoneDigits;
    const totals = selTotals;
    const clientName = selected.clientName || selected.client?.name || 'there';
    const validUntil = selected.validUntil ? new Date(selected.validUntil).toLocaleDateString('en-IN') : '';
    const message =
      `Hi ${clientName}, here is your quotation *${selected.quotationNumber}* from Creative Glass Tec.\n\n` +
      `Total Amount: ${fmtINR(totals.total)}\n` +
      (validUntil ? `Valid Until: ${validUntil}\n` : '') +
      `\nPlease reach out if you have any questions. Thank you!`;
    const url = `https://wa.me/${phone}?text=${encodeURIComponent(message)}`;
    window.open(url, '_blank', 'noopener,noreferrer');
  }

  function exportPDF(level = 'item') {
    if (!selected) return;
    const win = window.open('', '_blank');
    const notes = selected.notes
      ? selected.notes.split('\n').filter(Boolean).map((n, i) => `<li>${i + 1}. ${n}</li>`).join('')
      : '';
    // Recalc all totals from sections
    const sectionTotals = (selected.sections || []).map(sec =>
      (sec.items || []).reduce((s, i) => s + (+i.qty || 0) * (+i.rate || 0), 0)
    );
    const pdfSubTotal   = sectionTotals.reduce((s, t) => s + t, 0);
    const pdfGstPct     = selected.gstPercent || 18;
    const pdfTransport  = +(selected.transportation || 0);
    const pdfInstall    = +(selected.installation   || 0);
    const pdfGst        = +(pdfSubTotal * pdfGstPct / 100).toFixed(2);
    const pdfDisc       = +(selected.discount || 0);
    const pdfTotal      = +(pdfSubTotal + pdfTransport + pdfInstall + pdfGst - pdfDisc).toFixed(2);

    // level: 'category' = section totals only; 'item' = full line-item detail
    const rows = (selected.sections || []).map((sec, si) => {
      if (level === 'category') {
        return `<tr class="sec-head"><td>${si + 1}</td><td colspan="6"><b>${sec.title}</b></td><td>₹${Number(sectionTotals[si]).toLocaleString('en-IN')}</td></tr>`;
      }
      const itemRows = (sec.items || []).map((item, ii) => {
        const amt = (+item.qty || 0) * (+item.rate || 0);
        const area = (item.heightFt && item.widthFt) ? (item.heightFt * item.widthFt).toLocaleString('en-IN') : '—';
        return `<tr>
          <td>${si + 1}.${ii + 1}</td>
          <td>${item.description}</td>
          <td>${item.heightFt ?? '—'}</td>
          <td>${item.widthFt ?? '—'}</td>
          <td>${area}</td>
          <td>${item.qty}</td>
          <td>₹${Number(item.rate).toLocaleString('en-IN')}</td>
          <td>₹${Number(amt).toLocaleString('en-IN')}</td>
        </tr>`;
      }).join('');
      return `
        <tr class="sec-head"><td>${si + 1}</td><td colspan="6"><b>${sec.title}</b></td><td>₹${Number(sectionTotals[si]).toLocaleString('en-IN')}</td></tr>
        ${itemRows}`;
    }).join('');

    const logoUrl = `${window.location.origin}/cmpy_logo.png`;
    win.document.write(`<!DOCTYPE html><html><head><title>${selected.quotationNumber}</title><style>
      body{font-family:Arial,sans-serif;padding:24px;color:#222;font-size:13px}
      .header{display:flex;align-items:center;justify-content:space-between;border-bottom:2px solid #2563eb;padding-bottom:12px;margin-bottom:4px}
      .header-left{display:flex;align-items:center;gap:14px}
      .header-left img{height:56px;width:auto;object-fit:contain}
      .company-name{font-size:20px;font-weight:700;color:#0f172a}
      .company-sub{font-size:11px;color:#64748b;margin-top:2px}
      .quo-title{text-align:right}
      .quo-title h1{font-size:22px;font-weight:800;color:#2563eb;margin:0}
      .quo-title .quo-num{font-size:13px;color:#64748b;margin-top:2px}
      .quo-title .quo-status{display:inline-block;padding:2px 10px;border-radius:999px;font-size:11px;font-weight:700;background:#f0fdf4;color:#16a34a;border:1px solid #86efac;margin-top:4px}
      .meta{display:flex;gap:24px;margin:14px 0;padding:12px 16px;background:#f8fafc;border-radius:8px;border:1px solid #e2e8f0}
      .meta div{display:flex;flex-direction:column;gap:2px}
      .meta span{font-size:10px;color:#94a3b8;text-transform:uppercase;letter-spacing:.4px}
      .meta strong{font-size:13px;color:#0f172a}
      table{width:100%;border-collapse:collapse;margin:16px 0}
      th{background:#2563eb;color:#fff;padding:8px 10px;text-align:left;font-size:11px}
      td{padding:6px 10px;border-bottom:1px solid #f1f5f9;color:#334155}
      tr:nth-child(even) td{background:#fafafa}
      .sec-head td{background:#eff6ff;font-weight:700;color:#1e40af;font-size:12px;border-top:1px solid #bfdbfe}
      .subsec-head td{background:#f8fafc;font-weight:600;color:#475569;font-size:11px}
      .totals-wrap{display:flex;justify-content:flex-end;margin-top:8px}
      .totals{width:300px;border:1px solid #e2e8f0;border-radius:8px;overflow:hidden}
      .totals tr td{padding:7px 14px;border-bottom:1px solid #f1f5f9}
      .totals tr:last-child td{font-weight:700;color:#16a34a;font-size:15px;background:#f0fdf4;border-top:2px solid #86efac}
      .notes{margin-top:16px;padding:12px 16px;background:#fffbeb;border-radius:8px;border:1px solid #fde68a}
      .notes b{font-size:12px;color:#92400e}
      .notes ol{margin:6px 0 0;padding-left:18px;color:#78350f;line-height:1.7;font-size:12px}
      .footer{margin-top:32px;padding-top:12px;border-top:1px solid #e2e8f0;display:flex;justify-content:space-between;font-size:10px;color:#94a3b8}
      @media print{button{display:none}body{padding:16px}}
    </style></head><body>
      <div class="header">
        <div class="header-left">
          <img src="${logoUrl}" alt="Company Logo" onerror="this.style.display='none'" />
        </div>
        <div class="quo-title">
          <h1>QUOTATION</h1>
          <div class="quo-num"># ${selected.quotationNumber}</div>
          <span class="quo-status">${STATUS_CFG[selected.status]?.label || 'Draft'}</span>
        </div>
      </div>
      <div class="meta">
        <div><span>Customer</span><strong>${selected.clientName || '—'}</strong></div>
        <div><span>Project</span><strong>${selected.projectName || '—'}</strong></div>
        <div><span>Date</span><strong>${fmt(selected.date)}</strong></div>
        <div><span>Valid Until</span><strong>${fmt(selected.validUntil)}</strong></div>
      </div>
      <table>
        <thead>${level === 'item'
          ? '<tr><th>#</th><th>Description</th><th>Height (ft)</th><th>Width (ft)</th><th>Total Area (SQ/FT)</th><th>Qty</th><th>Rate</th><th>Amount</th></tr>'
          : '<tr><th>#</th><th colspan="6">Description</th><th>Amount</th></tr>'
        }</thead>
        <tbody>${rows}</tbody>
      </table>
      <div class="totals-wrap"><table class="totals">
        <tr><td>Sub Total</td><td>₹${Number(pdfSubTotal).toLocaleString('en-IN')}</td></tr>
        ${pdfTransport > 0 ? `<tr><td>Transportation</td><td>₹${Number(pdfTransport).toLocaleString('en-IN')}</td></tr>` : ''}
        ${pdfInstall   > 0 ? `<tr><td>Installation</td><td>₹${Number(pdfInstall).toLocaleString('en-IN')}</td></tr>` : ''}
        <tr><td>GST (${pdfGstPct}%)</td><td>₹${Number(pdfGst).toLocaleString('en-IN')}</td></tr>
        ${pdfDisc > 0 ? `<tr><td>Discount</td><td>-₹${Number(pdfDisc).toLocaleString('en-IN')}</td></tr>` : ''}
        <tr><td>Total Amount</td><td>₹${Number(pdfTotal).toLocaleString('en-IN')}</td></tr>
      </table></div>
      ${notes ? `<div class="notes"><b>Notes</b><ol>${notes}</ol></div>` : ''}
      <div class="footer">
        <span>Generated on ${new Date().toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' })}</span>
        <span>This is a computer generated quotation</span>
      </div>
      <script>window.onload=()=>{window.print();}${'<'}/script>
    </body></html>`);
    win.document.close();
  }

  // ── Inline item edit in detail view ──
  function startEditItem(si, ii, item) {
    setEditingItem({ si, ii });
    if (item.subRows && item.subRows.length) {
      setEditingItemData({
        description: item.description,
        unit: item.unit,
        rate: item.rate,
        subRows: item.subRows.map(r => ({ heightFt: r.heightFt ?? '', widthFt: r.widthFt ?? '' })),
      });
    } else {
      setEditingItemData({ description: item.description, heightFt: item.heightFt ?? '', widthFt: item.widthFt ?? '', unit: item.unit, qty: item.qty, rate: item.rate });
    }
  }

  function updateEditingSubRow(ri, k, v) {
    setEditingItemData(d => ({
      ...d,
      subRows: d.subRows.map((r, i) => i !== ri ? r : { ...r, [k]: v }),
    }));
  }

  function removeEditingSubRow(ri) {
    setEditingItemData(d => ({ ...d, subRows: d.subRows.filter((_, i) => i !== ri) }));
  }

  async function saveEditItem() {
    if (!selected || !editingItem) return;
    const { si, ii } = editingItem;
    const isMerged = !!editingItemData.subRows;
    const subRows = isMerged
      ? editingItemData.subRows.map(r => ({ heightFt: +r.heightFt || 0, widthFt: +r.widthFt || 0 }))
      : undefined;
    const qty = isMerged
      ? +subRows.reduce((s, r) => s + (r.heightFt * r.widthFt), 0).toFixed(2)
      : +editingItemData.qty;
    const sections = selected.sections.map((sec, s) =>
      s !== si ? sec : {
        ...sec,
        items: sec.items.map((item, i) => i !== ii ? item : {
          ...item,
          description: editingItemData.description,
          heightFt: isMerged ? undefined : (editingItemData.heightFt === '' ? undefined : +editingItemData.heightFt),
          widthFt: isMerged ? undefined : (editingItemData.widthFt === '' ? undefined : +editingItemData.widthFt),
          subRows,
          unit: editingItemData.unit,
          qty,
          rate: +editingItemData.rate,
        }),
      }
    );
    try {
      const r = await API.put(`/quotations/${selected._id}`, { ...selected, sections });
      setQuotations(prev => prev.map(q => q._id === r.data._id ? r.data : q));
      setSelected(r.data);
      setEditingItem(null);
      toast.success('Item updated');
    } catch { toast.error('Update failed'); }
  }

  async function deleteItem(si, ii) {
    if (!selected) return;
    const ok = await askConfirm({ title: 'Delete Item', message: 'Remove this item from the quotation?', confirmLabel: 'Delete', confirmColor: '#dc2626', icon: '🗑️' });
    if (!ok) return;
    const sections = selected.sections.map((sec, s) =>
      s !== si ? sec : { ...sec, items: sec.items.filter((_, i) => i !== ii) }
    );
    try {
      const r = await API.put(`/quotations/${selected._id}`, { ...selected, sections });
      setQuotations(prev => prev.map(q => q._id === r.data._id ? r.data : q));
      setSelected(r.data);
      toast.success('Item deleted');
    } catch { toast.error('Delete failed'); }
  }

  function toggleMergeSelect(si, ii) {
    setMergeSelection(prev => {
      const already = prev.some(s => s.si === si && s.ii === ii);
      if (already) return prev.filter(s => !(s.si === si && s.ii === ii));
      // Only allow selecting rows within the same section
      if (prev.length && prev[0].si !== si) return [{ si, ii }];
      return [...prev, { si, ii }];
    });
  }

  async function mergeSelectedItems() {
    if (!selected || mergeSelection.length < 2) return;
    const si = mergeSelection[0].si;
    const idxs = mergeSelection.map(s => s.ii).sort((a, b) => a - b);
    const sec = selected.sections[si];
    const chosenItems = idxs.map(i => sec.items[i]);

    const desc = chosenItems[0].description;
    if (!chosenItems.every(it => it.description?.trim() === desc?.trim())) {
      toast.error('Selected items must have the same description to merge');
      return;
    }

    const subRows = chosenItems.flatMap(it =>
      it.subRows?.length ? it.subRows : [{ heightFt: it.heightFt || 0, widthFt: it.widthFt || 0 }]
    );
    const mergedItem = {
      description: desc,
      unit: chosenItems[0].unit || 'Nos',
      rate: chosenItems[0].rate || 0,
      subRows,
      heightFt: undefined,
      widthFt: undefined,
      qty: +subRows.reduce((s, r) => s + ((+r.heightFt || 0) * (+r.widthFt || 0)), 0).toFixed(2),
    };

    const keepIdx = idxs[0];
    const rebuilt = [];
    sec.items.forEach((it, i) => {
      if (i === keepIdx) rebuilt.push(mergedItem);
      else if (!idxs.includes(i)) rebuilt.push(it);
    });

    const sections = selected.sections.map((s, i) => i !== si ? s : { ...s, items: rebuilt });
    try {
      const r = await API.put(`/quotations/${selected._id}`, { ...selected, sections });
      setQuotations(prev => prev.map(q => q._id === r.data._id ? r.data : q));
      setSelected(r.data);
      setMergeSelection([]);
      toast.success('Items merged');
    } catch { toast.error('Merge failed'); }
  }

  async function deleteSection(si) {
    if (!selected) return;
    const ok = await askConfirm({ title: 'Delete Section', message: `Delete "${selected.sections[si]?.title}" and all its items? This cannot be undone.`, confirmLabel: 'Delete', confirmColor: '#dc2626', icon: '⚠️' });
    if (!ok) return;
    const sections = selected.sections.filter((_, s) => s !== si);
    try {
      const r = await API.put(`/quotations/${selected._id}`, { ...selected, sections });
      setQuotations(prev => prev.map(q => q._id === r.data._id ? r.data : q));
      setSelected(r.data);
      toast.success('Section deleted');
    } catch { toast.error('Delete failed'); }
  }

  async function importFromMaterialPlan() {
    if (!selected) return;
    const projectId = selected.project?._id || selected.project;
    if (!projectId) return toast.error('Quotation is not linked to a project');
    try {
      const r = await API.get(`/material-planning/${projectId}`);
      const plan = r.data;
      if (!plan || !plan.categories?.length) return toast.info('No material plan found for this project');

      const existingTitles = new Set((selected.sections || []).map(s => s.title));
      const newSections = plan.categories
        .filter(cat => !existingTitles.has(cat.title))
        .map(cat => ({
          title: cat.title,
          items: catToItems(cat),
        }));
      if (!newSections.length) return toast.info('All material plan categories are already in this BOQ');

      const sections = [...(selected.sections || []), ...newSections];
      const res = await API.put(`/quotations/${selected._id}`, { ...selected, sections });
      setQuotations(prev => prev.map(q => q._id === res.data._id ? res.data : q));
      setSelected(res.data);
      toast.success(`Imported ${newSections.length} section(s) from Material Plan`);
    } catch { toast.error('Failed to import from Material Plan'); }
  }

  function initials(name) {
    if (!name) return '?';
    return name.split(' ').slice(0, 2).map(w => w[0]).join('').toUpperCase();
  }

  // Always recompute from sections so stale DB totalAmount never shows ₹0
  const selTotals = (() => {
    if (!selected) return { sub: 0, gst: 0, transport: 0, install: 0, discount: 0, total: 0 };
    const sub = (selected.sections || []).reduce((s, sec) =>
      s + (sec.items || []).reduce((ss, i) => ss + (+i.qty || 0) * (+i.rate || 0), 0), 0);
    const transport = +(selected.transportation || 0);
    const install   = +(selected.installation   || 0);
    const gst       = +(sub * ((selected.gstPercent || 18) / 100));
    const discount  = +(selected.discount || 0);
    const total     = sub + transport + install + gst - discount;
    return { sub, gst, transport, install, discount, total };
  })();

  return (
    <Layout>
      {loading && <PageLoader text="Loading quotations..." />}
      <div className="quo-root" onClick={() => { setMoreMenuId(null); if (showFilter) setShowFilter(false); }}>
        <div className="quo-layout">

          {/* ── LEFT LIST ── */}
          <div className="quo-list-col">
            <div className="quo-list-header">
              <span className="quo-list-title">All Quotations <span className="quo-count">{filtered.length}</span></span>
            </div>
            <div className="quo-list-search">
              <div className="quo-search-box">
                <FiSearch size={13} className="quo-search-icon" />
                <input placeholder="Search quotations..." value={search}
                  onChange={e => { setSearch(e.target.value); setPage(1); }} />
              </div>
              <div style={{ position: 'relative' }}>
                <button className={`quo-filter-btn ${filterStatus ? 'quo-filter-active' : ''}`}
                  onClick={e => { e.stopPropagation(); setShowFilter(v => !v); }}>
                  <FiFilter size={13} /> Filter {filterStatus && `(${STATUS_CFG[filterStatus]?.label})`}
                </button>
                {showFilter && (
                  <div className="quo-filter-dropdown" onClick={e => e.stopPropagation()}>
                    <button className={!filterStatus ? 'quo-fd-active' : ''} onClick={() => { setFilterStatus(''); setPage(1); setShowFilter(false); }}>All</button>
                    {Object.entries(STATUS_CFG).map(([k, v]) => (
                      <button key={k} className={filterStatus === k ? 'quo-fd-active' : ''}
                        onClick={() => { setFilterStatus(k); setPage(1); setShowFilter(false); }}
                        style={{ color: v.color }}>{v.label}</button>
                    ))}
                  </div>
                )}
              </div>
            </div>
            <div className="quo-list-items">
              {paginated.length === 0 && <p className="quo-list-empty">No quotations found</p>}
              {paginated.map(q => (
                <div key={q._id}
                  className={`quo-list-item ${selected?._id === q._id ? 'quo-item-selected' : ''}`}
                  onClick={() => selectQuotation(q)}
                >
                  <div className="quo-item-top">
                    <span className="quo-item-id">{q.quotationNumber}</span>
                    <StatusBadge status={q.status} />
                  </div>
                  <div className="quo-item-client">{q.clientName || q.client?.name || '—'}</div>
                  <div className="quo-item-bottom">
                    <span className="quo-item-project">{q.projectName || q.project?.title || '—'}</span>
                    <span className="quo-item-amount">{fmtINR((() => {
                      const sub = (q.sections||[]).reduce((s,sec) => s+(sec.items||[]).reduce((ss,i)=>ss+(+i.qty||0)*(+i.rate||0),0),0);
                      return sub + (q.transportation||0) + (q.installation||0) + sub*((q.gstPercent||18)/100) - (q.discount||0);
                    })())}</span>
                  </div>
                  <div className="quo-item-date">{fmt(q.date)}</div>
                </div>
              ))}
            </div>
            <div className="quo-list-pagination">
              <button className="quo-page-btn" disabled={page <= 1} onClick={() => setPage(p => p - 1)}>‹</button>
              {Array.from({ length: totalPages }, (_, i) => i + 1).map(p => (
                <button key={p} className={`quo-page-btn ${page === p ? 'quo-page-active' : ''}`} onClick={() => setPage(p)}>{p}</button>
              ))}
              <button className="quo-page-btn" disabled={page >= totalPages} onClick={() => setPage(p => p + 1)}>›</button>
            </div>
          </div>

          {/* ── RIGHT DETAIL ── */}
          <div className="quo-detail-col">
            {/* Action bar */}
            <div className="quo-action-bar" onClick={e => e.stopPropagation()}>
              <button className="quo-btn-primary" onClick={() => { setEditQuotation(null); setShowModal(true); }}>
                <FiPlus size={13} /> New Quotation
              </button>
              <button className="quo-btn-outline quo-btn-pdf" onClick={() => exportPDF('item')} disabled={!selected}>
                <FiFileText size={13} color="#dc2626" /> Export PDF
              </button>
              {selected && (
                <>
                  <div style={{ position: 'relative' }}>
                    <button className="quo-btn-outline" onClick={() => setMoreMenuId(moreMenuId ? null : selected._id)}>
                      <FiMoreHorizontal size={13} /> More <FiChevronDown size={11} />
                    </button>
                    {moreMenuId === selected._id && (
                      <div className="quo-more-menu" onClick={e => e.stopPropagation()}>
                        <button onClick={() => { setEditQuotation(selected); setShowModal(true); setMoreMenuId(null); }}><FiEdit2 size={12}/> Edit Quotation</button>
                        {selected.status !== 'approved' && <button onClick={() => confirmAndUpdateStatus(selected._id, 'approved')}><FiCheck size={12}/> Mark Approved</button>}
                        {selected.status !== 'rejected' && <button onClick={() => handleRejectClick(selected._id)} className="quo-menu-danger"><FiX size={12}/> Mark Rejected</button>}
                        <button onClick={() => { exportPDF('item'); setMoreMenuId(null); }}><FiPrinter size={12}/> Print / PDF</button>
                        <button onClick={() => del(selected._id)} className="quo-menu-danger"><FiTrash2 size={12}/> Delete</button>
                      </div>
                    )}
                  </div>
                </>
              )}
            </div>

            {!selected ? (
              <div className="quo-empty-state">
                <FiFileText size={40} color="#cbd5e1" />
                <p>Select a quotation to view details</p>
                <button className="quo-btn-primary" onClick={() => { setEditQuotation(null); setShowModal(true); }}>
                  <FiPlus size={13} /> New Quotation
                </button>
              </div>
            ) : (
              <div className="quo-detail-body">
                {/* Header */}
                <div className="quo-detail-header">
                  <div className="quo-detail-id-row">
                    <h2 className="quo-detail-id">Quotation # {selected.quotationNumber}</h2>
                    <StatusBadge status={selected.status} />
                    {selected.status === 'rejected' && selected.rejectionReason && (
                      <span style={{ fontSize: '.75rem', color: '#dc2626', background: '#fef2f2', border: '1px solid #fecaca', borderRadius: 6, padding: '2px 8px', marginLeft: 4 }}>
                        Reason: {selected.rejectionReason}
                      </span>
                    )}
                    <div style={{ marginLeft: 'auto', display: 'flex', gap: '.5rem' }}>
                      <button className="quo-btn-sm quo-btn-outline" onClick={() => { setEditQuotation(selected); setShowModal(true); }}>
                        <FiEdit2 size={11}/> Edit
                      </button>
                      <button className="quo-btn-sm quo-btn-outline quo-btn-pdf" onClick={() => exportPDF('item')}>
                        <FiDownload size={11}/> PDF
                      </button>
                    </div>
                  </div>
                  <div className="quo-meta-strip">
                    <div className="quo-meta-avatar">{initials(selected.clientName || selected.client?.name)}</div>
                    <div className="quo-meta-block"><span>Customer</span><strong>{selected.clientName || selected.client?.name || '—'}</strong></div>
                    <div className="quo-meta-sep" />
                    <div className="quo-meta-block"><span>Project</span><strong>{selected.projectName || selected.project?.title || '—'}</strong></div>
                    <div className="quo-meta-sep" />
                    <div className="quo-meta-block"><span>Date</span><strong>{fmt(selected.date)}</strong></div>
                    <div className="quo-meta-sep" />
                    <div className="quo-meta-block"><span>Valid Until</span><strong>{fmt(selected.validUntil)}</strong></div>
                    <div className="quo-meta-total">
                      <span>Total Amount</span>
                      <strong className="quo-total-big">{fmtINR(selTotals.total)}</strong>
                      <small>(Inclusive of all taxes)</small>
                    </div>
                  </div>
                </div>

                {/* Timeline */}
                {(() => {
                  const TL_STEPS  = ['draft', 'approved', 'rejected'];
                  const TL_ICONS  = { draft: <FiFileText size={13}/>, sent: <FiSend size={13}/>, approved: <FiCheck size={13}/>, rejected: <FiX size={13}/> };
                  const TL_COLORS = { draft: '#64748b', sent: '#2563eb', approved: '#16a34a', rejected: '#dc2626' };
                  const cur = selected.status;
                  const histMap = {};
                  const histUser = {};
                  (selected.statusHistory || []).forEach(h => {
                    histMap[h.status]  = h.changedAt;
                    histUser[h.status] = h.changedBy?.name || null;
                  });
                  if (!histMap['draft']) histMap['draft'] = selected.createdAt;
                  if (!histMap[cur] && cur !== 'draft') histMap[cur] = selected.updatedAt;

                  const fmtDT = d => {
                    if (!d) return '–';
                    return new Date(d).toLocaleString('en-IN', { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit', hour12: true });
                  };

                  const linearOrder = ['draft', 'sent', 'approved'];
                  const isDone = step => {
                    if (step === 'rejected') return cur === 'rejected';
                    if (cur === 'rejected') return step === 'draft';
                    return linearOrder.indexOf(step) <= linearOrder.indexOf(cur);
                  };

                  const nextStatuses = { draft: ['approved','rejected'], sent: ['approved','rejected'], approved: ['rejected'], rejected: ['approved'] }[cur] || [];

                  return (
                    <div className="quo-timeline">
                      {TL_STEPS.map((step, i) => {
                        const active   = step === cur;
                        const done     = isDone(step);
                        const canClick = nextStatuses.includes(step);
                        const color    = TL_COLORS[step];
                        return (
                          <React.Fragment key={step}>
                            {i > 0 && (
                              <div className={`quo-tl-line ${step === 'rejected' ? 'quo-tl-line-branch' : ''} ${done ? (step === 'rejected' ? 'quo-tl-done-red' : 'quo-tl-done') : ''}`} />
                            )}
                            <div
                              className={`quo-tl-step ${active ? 'quo-tl-active' : ''} ${done ? 'quo-tl-past' : ''} ${canClick ? 'quo-tl-clickable' : ''}`}
                              onClick={canClick ? () => {
                                if (step === 'rejected') handleRejectClick(selected._id);
                                else if (step === 'sent') beginSendQuotation();
                                else confirmAndUpdateStatus(selected._id, step);
                              } : undefined}
                              title={canClick ? `Click to mark as ${STATUS_CFG[step].label}` : undefined}
                            >
                              <div className="quo-tl-icon" style={{
                                borderColor: (done || active) ? color : '#e2e8f0',
                                background:  active ? color : done ? color + '20' : '#fff',
                                color:       active ? '#fff' : (done || active) ? color : '#94a3b8',
                              }}>
                                {TL_ICONS[step]}
                              </div>
                              <span className="quo-tl-label" style={{ color: active ? color : done ? '#334155' : '#94a3b8', fontWeight: active ? 700 : 500 }}>
                                {STATUS_CFG[step].label}
                              </span>
                              <span className="quo-tl-date">
                                {histMap[step] ? fmtDT(histMap[step]) : canClick ? <em className="quo-tl-hint">click to set</em> : '–'}
                              </span>
                              {histMap[step] && histUser[step] && (
                                <span className="quo-tl-user">by {histUser[step]}</span>
                              )}
                            </div>
                          </React.Fragment>
                        );
                      })}
                    </div>
                  );
                })()}

                {/* BOQ Layout */}
                <div className="quo-boq-layout">
                  {/* LEFT: section summary */}
                  <div className="quo-sections-panel">
                    <div className="quo-sp-header">
                      <span className="quo-sp-title">BOQ (Bill of Quantity)</span>
                      <button className="quo-sp-add-section" onClick={() => { setEditQuotation(selected); setShowModal(true); }}>
                        <FiPlus size={11} /> Add Section
                      </button>
                    </div>
                    <div className="quo-section-list">
                      {(selected.sections || []).map((sec, si) => (
                        <div key={si} className="quo-section-row">
                          <span className="quo-sec-num">{si + 1}</span>
                          <span className="quo-sec-name">{sec.title}</span>
                          <span className="quo-sec-amt">{fmtINR(sec.sectionTotal)}</span>
                          <button className="quo-sec-del-btn" title="Delete section" onClick={() => deleteSection(si)}><FiTrash2 size={10}/></button>
                        </div>
                      ))}
                    </div>
                    <button className="quo-add-custom-sec" onClick={() => { setEditQuotation(selected); setShowModal(true); }}>
                      <FiPlus size={11} /> Add Custom Section
                    </button>
                    <div className="quo-summary-box">
                      <h4 className="quo-sum-title">Quotation Summary</h4>
                      <div className="quo-sum-row"><span>Sub Total</span><span>{fmtINR(selTotals.sub)}</span></div>
                      {selTotals.transport > 0 && <div className="quo-sum-row"><span>Transportation</span><span>{fmtINR(selTotals.transport)}</span></div>}
                      {selTotals.install   > 0 && <div className="quo-sum-row"><span>Installation</span><span>{fmtINR(selTotals.install)}</span></div>}
                      <div className="quo-sum-row"><span>GST ({selected.gstPercent || 18}%)</span><span>{fmtINR(selTotals.gst)}</span></div>
                      {selTotals.discount  > 0 && <div className="quo-sum-row"><span>Discount</span><span className="quo-discount">-{fmtINR(selTotals.discount)}</span></div>}
                      <div className="quo-sum-total"><span>Total Amount</span><span className="quo-sum-total-amt">{fmtINR(selTotals.total)}</span></div>
                    </div>
                    {selected.notes && (
                      <div className="quo-notes-box">
                        <div className="quo-notes-icon">📋</div>
                        <div>
                          <p className="quo-notes-title">Notes</p>
                          <ol className="quo-notes-list">
                            {selected.notes.split('\n').filter(Boolean).map((n, i) => <li key={i}>{n}</li>)}
                          </ol>
                        </div>
                      </div>
                    )}
                  </div>

                  {/* RIGHT: items table with inline edit */}
                  <div className="quo-items-panel">
                    <div className="quo-items-actions">
                      <button className="quo-btn-sm quo-btn-outline" onClick={() => { setEditQuotation(selected); setShowModal(true); }}><FiPlus size={11}/> Add Section / Item</button>
                      <button className="quo-btn-sm quo-btn-outline" onClick={importFromMaterialPlan}><FiDownload size={11}/> Import from Material Plan</button>
                      {mergeSelection.length >= 2 && (
                        <button className="quo-btn-sm quo-btn-outline" onClick={mergeSelectedItems}>Merge Selected ({mergeSelection.length})</button>
                      )}
                    </div>
                    <div className="quo-items-table-wrap">
                    <table className="quo-items-table">
                      <thead>
                        <tr><th></th><th>#</th><th>Item / Description</th><th>Height (ft)</th><th>Width (ft)</th><th>Total Area (SQ/FT)</th><th>Qty</th><th>Rate (₹)</th><th>Amount (₹)</th><th>Action</th></tr>
                      </thead>
                      <tbody>
                        {(selected.sections || []).map((sec, si) => (
                          <React.Fragment key={si}>
                            <tr className="quo-sec-heading-row">
                              <td colSpan={2}>{si + 1}</td>
                              <td colSpan={6}><strong>{sec.title}</strong></td>
                              <td className="quo-sec-total">{fmtINR(sec.sectionTotal)}</td>
                              <td>
                                <button className="quo-icon-btn quo-icon-del" title="Delete section" onClick={() => deleteSection(si)}><FiTrash2 size={11}/></button>
                              </td>
                            </tr>
                            {(sec.items || []).map((item, ii) => {
                              const isEditing = editingItem?.si === si && editingItem?.ii === ii;
                              const isMerged = item.subRows && item.subRows.length > 0;
                              const checked = mergeSelection.some(s => s.si === si && s.ii === ii);
                              return isEditing && editingItemData.subRows ? (
                                <React.Fragment key={ii}>
                                  {editingItemData.subRows.map((row, ri) => {
                                    const n = editingItemData.subRows.length;
                                    const editQty = +editingItemData.subRows.reduce((s, r) => s + ((+r.heightFt || 0) * (+r.widthFt || 0)), 0).toFixed(2);
                                    return (
                                      <tr key={ri} className="quo-item-row quo-editing-row quo-subrow">
                                        {ri === 0 && <td rowSpan={n}></td>}
                                        {ri === 0 && <td className="quo-td-itemno" rowSpan={n}>{si + 1}.{ii + 1}</td>}
                                        {ri === 0 && (
                                          <td rowSpan={n}>
                                            <input className="quo-inline-input" value={editingItemData.description} onChange={e => setEditingItemData(d => ({ ...d, description: e.target.value }))} />
                                          </td>
                                        )}
                                        <td><input className="quo-inline-input quo-inline-num" type="number" min="0" step="0.01" value={row.heightFt} onChange={e => updateEditingSubRow(ri, 'heightFt', e.target.value)} /></td>
                                        <td><input className="quo-inline-input quo-inline-num" type="number" min="0" step="0.01" value={row.widthFt} onChange={e => updateEditingSubRow(ri, 'widthFt', e.target.value)} /></td>
                                        <td className="quo-editor-amt">
                                          {(row.heightFt && row.widthFt) ? (row.heightFt * row.widthFt).toLocaleString('en-IN') : '–'}
                                          {n > 1 && <button className="quo-icon-btn quo-icon-del" title="Remove size" onClick={() => removeEditingSubRow(ri)}><FiX size={10}/></button>}
                                        </td>
                                        {ri === 0 && <td rowSpan={n}>{editQty}</td>}
                                        {ri === 0 && (
                                          <td rowSpan={n}>
                                            <input className="quo-inline-input quo-inline-num" type="number" min="0" value={editingItemData.rate} onChange={e => setEditingItemData(d => ({ ...d, rate: e.target.value }))} />
                                          </td>
                                        )}
                                        {ri === 0 && <td className="quo-editor-amt" rowSpan={n}>{fmtINR(editQty * (+editingItemData.rate || 0))}</td>}
                                        {ri === 0 && (
                                          <td rowSpan={n}>
                                            <div className="quo-item-acts">
                                              <button className="quo-icon-btn" title="Add size" onClick={() => setEditingItemData(d => ({ ...d, subRows: [...d.subRows, { heightFt: '', widthFt: '' }] }))}><FiPlus size={12}/></button>
                                              <button className="quo-icon-btn quo-icon-save" title="Save" onClick={saveEditItem}><FiCheck size={12}/></button>
                                              <button className="quo-icon-btn" title="Cancel" onClick={() => setEditingItem(null)}><FiX size={12}/></button>
                                            </div>
                                          </td>
                                        )}
                                      </tr>
                                    );
                                  })}
                                </React.Fragment>
                              ) : isEditing ? (
                                <tr key={ii} className="quo-item-row quo-editing-row">
                                  <td></td>
                                  <td className="quo-td-itemno">{si + 1}.{ii + 1}</td>
                                  <td><input className="quo-inline-input" value={editingItemData.description} onChange={e => setEditingItemData(d => ({ ...d, description: e.target.value }))} /></td>
                                  <td><input className="quo-inline-input quo-inline-num" type="number" min="0" step="0.01" value={editingItemData.heightFt} onChange={e => setEditingItemData(d => ({ ...d, heightFt: e.target.value }))} /></td>
                                  <td><input className="quo-inline-input quo-inline-num" type="number" min="0" step="0.01" value={editingItemData.widthFt} onChange={e => setEditingItemData(d => ({ ...d, widthFt: e.target.value }))} /></td>
                                  <td className="quo-editor-amt">{(editingItemData.heightFt && editingItemData.widthFt) ? (editingItemData.heightFt * editingItemData.widthFt).toLocaleString('en-IN') : '–'}</td>
                                  <td><input className="quo-inline-input quo-inline-num" type="number" min="0" value={editingItemData.qty} onChange={e => setEditingItemData(d => ({ ...d, qty: e.target.value }))} /></td>
                                  <td><input className="quo-inline-input quo-inline-num" type="number" min="0" value={editingItemData.rate} onChange={e => setEditingItemData(d => ({ ...d, rate: e.target.value }))} /></td>
                                  <td className="quo-editor-amt">{fmtINR((+editingItemData.qty || 0) * (+editingItemData.rate || 0))}</td>
                                  <td>
                                    <div className="quo-item-acts">
                                      <button className="quo-icon-btn quo-icon-save" title="Save" onClick={saveEditItem}><FiCheck size={12}/></button>
                                      <button className="quo-icon-btn" title="Cancel" onClick={() => setEditingItem(null)}><FiX size={12}/></button>
                                    </div>
                                  </td>
                                </tr>
                              ) : isMerged ? (
                                <React.Fragment key={ii}>
                                  {item.subRows.map((row, ri) => (
                                    <tr key={ri} className="quo-item-row quo-subrow">
                                      {ri === 0 && (
                                        <td rowSpan={item.subRows.length}>
                                          <input type="checkbox" checked={checked} onChange={() => toggleMergeSelect(si, ii)} />
                                        </td>
                                      )}
                                      {ri === 0 && <td className="quo-td-itemno" rowSpan={item.subRows.length}>{si + 1}.{ii + 1}</td>}
                                      {ri === 0 && <td className="quo-td-name" rowSpan={item.subRows.length}>{item.description}</td>}
                                      <td>{row.heightFt ?? '–'}</td>
                                      <td>{row.widthFt ?? '–'}</td>
                                      <td>{(row.heightFt && row.widthFt) ? (row.heightFt * row.widthFt).toLocaleString('en-IN') : '–'}</td>
                                      {ri === 0 && <td rowSpan={item.subRows.length}>{item.qty}</td>}
                                      {ri === 0 && <td rowSpan={item.subRows.length}>{Number(item.rate).toLocaleString('en-IN')}</td>}
                                      {ri === 0 && <td rowSpan={item.subRows.length}>{Number(item.amount).toLocaleString('en-IN')}</td>}
                                      {ri === 0 && (
                                        <td rowSpan={item.subRows.length}>
                                          <div className="quo-item-acts">
                                            <button className="quo-icon-btn" title="Edit item" onClick={() => startEditItem(si, ii, item)}><FiEdit2 size={12}/></button>
                                            <button className="quo-icon-btn quo-icon-del" title="Delete item" onClick={() => deleteItem(si, ii)}><FiTrash2 size={12}/></button>
                                          </div>
                                        </td>
                                      )}
                                    </tr>
                                  ))}
                                </React.Fragment>
                              ) : (
                                <tr key={ii} className="quo-item-row">
                                  <td><input type="checkbox" checked={checked} onChange={() => toggleMergeSelect(si, ii)} /></td>
                                  <td className="quo-td-itemno">{si + 1}.{ii + 1}</td>
                                  <td className="quo-td-name">{item.description}</td>
                                  <td>{item.heightFt ?? '–'}</td>
                                  <td>{item.widthFt ?? '–'}</td>
                                  <td>{(item.heightFt && item.widthFt) ? (item.heightFt * item.widthFt).toLocaleString('en-IN') : '–'}</td>
                                  <td>{item.qty}</td>
                                  <td>{Number(item.rate).toLocaleString('en-IN')}</td>
                                  <td>{Number(item.amount).toLocaleString('en-IN')}</td>
                                  <td>
                                    <div className="quo-item-acts">
                                      <button className="quo-icon-btn" title="Edit item" onClick={() => startEditItem(si, ii, item)}><FiEdit2 size={12}/></button>
                                      <button className="quo-icon-btn quo-icon-del" title="Delete item" onClick={() => deleteItem(si, ii)}><FiTrash2 size={12}/></button>
                                    </div>
                                  </td>
                                </tr>
                              );
                            })}
                          </React.Fragment>
                        ))}
                        {(selected.sections || []).length === 0 && (
                          <tr><td colSpan={10} className="quo-table-empty">No BOQ items. Click "+ Add Section / Item" to start.</td></tr>
                        )}
                        <tr className="quo-subtotal-row">
                          <td colSpan={8}>Sub Total</td>
                          <td className="quo-subtotal-amt">{fmtINR(selTotals.sub)}</td>
                          <td />
                        </tr>
                        {selTotals.transport > 0 && (
                          <tr className="quo-subtotal-row">
                            <td colSpan={8}>Transportation</td>
                            <td className="quo-subtotal-amt">{fmtINR(selTotals.transport)}</td>
                            <td />
                          </tr>
                        )}
                        {selTotals.install > 0 && (
                          <tr className="quo-subtotal-row">
                            <td colSpan={8}>Installation</td>
                            <td className="quo-subtotal-amt">{fmtINR(selTotals.install)}</td>
                            <td />
                          </tr>
                        )}
                        <tr className="quo-subtotal-row">
                          <td colSpan={8}>GST ({selected.gstPercent || 18}%)</td>
                          <td className="quo-subtotal-amt">{fmtINR(selTotals.gst)}</td>
                          <td />
                        </tr>
                        {selTotals.discount > 0 && (
                          <tr className="quo-subtotal-row quo-discount-row">
                            <td colSpan={8}>Discount</td>
                            <td className="quo-subtotal-amt quo-discount">-{fmtINR(selTotals.discount)}</td>
                            <td />
                          </tr>
                        )}
                        <tr className="quo-grand-total-row">
                          <td colSpan={8}><strong>Grand Total</strong></td>
                          <td className="quo-grand-amt">{fmtINR(selTotals.total)}</td>
                          <td />
                        </tr>
                      </tbody>
                    </table>
                    </div>
                  </div>
                </div>

                <div className="quo-tip">
                  ℹ️ Click the edit icon on any item to edit inline. Use the trash icon to delete. Add new sections/items via the button above.
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      {confirm && <ConfirmDialog {...confirm} />}

      {showSendChoice && (
        <div className="quo-overlay" onClick={() => !sending && setShowSendChoice(false)}>
          <div className="quo-confirm-box" onClick={e => e.stopPropagation()} style={{ maxWidth: 380 }}>
            <h4 className="quo-confirm-title">Send Quotation</h4>
            <p className="quo-confirm-msg">Choose how much detail to include in the attached PDF.</p>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '.5rem', margin: '1rem 0' }}>
              <button className="quo-btn-outline" style={{ justifyContent: 'flex-start' }} disabled={sending} onClick={async () => { setShowSendChoice(false); await sendQuotation('category'); }}>
                Category-wise <span style={{ marginLeft: 'auto', color: '#94a3b8', fontSize: '.72rem' }}>Section totals only</span>
              </button>
              <button className="quo-btn-outline" style={{ justifyContent: 'flex-start' }} disabled={sending} onClick={async () => { setShowSendChoice(false); await sendQuotation('item'); }}>
                Item-wise <span style={{ marginLeft: 'auto', color: '#94a3b8', fontSize: '.72rem' }}>Full line-item detail</span>
              </button>
            </div>
            <div className="quo-confirm-actions">
              <button className="quo-confirm-cancel" onClick={() => setShowSendChoice(false)} disabled={sending}>Cancel</button>
            </div>
          </div>
        </div>
      )}

      {showRejectModal && (
        <div className="quo-overlay">
          <div className="rj-modal" onClick={e => e.stopPropagation()}>
            {/* Icon header */}
            <div className="rj-modal-top">
              <div className="rj-icon-wrap">
                <FiX size={22} />
              </div>
              <button className="rj-close-btn" onClick={() => setShowRejectModal(false)}><FiX size={16}/></button>
            </div>
            <div className="rj-modal-body">
              <h3 className="rj-title">Mark as Rejected</h3>
              <p className="rj-subtitle">Please provide a reason for rejecting this quotation. This will be saved and visible in the quotation history.</p>

              <label className="rj-label">Rejection Reason <span className="rj-required">*</span></label>
              <textarea
                className="rj-textarea"
                rows={3}
                placeholder="e.g. Budget exceeded, scope changes required, client not interested…"
                value={rejectReason}
                onChange={e => setRejectReason(e.target.value)}
                autoFocus
              />

              {/* Email notify toggle */}
              <div className={`rj-notify-row ${!rejectClientEmail ? 'rj-notify-disabled' : ''}`}>
                <div className="rj-notify-info">
                  <div className="rj-notify-label">
                    <FiSend size={13}/>
                    Notify customer via email
                  </div>
                  <div className="rj-notify-email">
                    {rejectClientEmail || 'No email on file'}
                  </div>
                </div>
                <label className="rj-toggle">
                  <input
                    type="checkbox"
                    checked={rejectNotify}
                    disabled={!rejectClientEmail}
                    onChange={e => setRejectNotify(e.target.checked)}
                  />
                  <span className="rj-toggle-track" />
                </label>
              </div>
            </div>
            <div className="rj-modal-footer">
              <button className="rj-btn-cancel" onClick={() => setShowRejectModal(false)}>Cancel</button>
              <button className="rj-btn-confirm" onClick={submitRejection}>
                <FiX size={14}/> Confirm Rejection
              </button>
            </div>
          </div>
        </div>
      )}

      {showModal && (
        <QuotationModal
          quotation={editQuotation}
          clients={clients}
          projects={projects}
          prefilledClient={prefilledClient}
          prefilledProject={prefilledProject}
          onClose={() => { setShowModal(false); setEditQuotation(null); setPrefilledClient(null); setPrefilledProject(null); }}
          onSaved={(q) => {
            setQuotations(prev => editQuotation ? prev.map(x => x._id === q._id ? q : x) : [q, ...prev]);
            setSelected(q);
            setShowModal(false);
            setEditQuotation(null);
            setPrefilledClient(null);
            setPrefilledProject(null);
            toast.success(editQuotation ? 'Quotation updated' : 'Quotation created');
          }}
        />
      )}
    </Layout>
  );
}

/* ─────────────────────────────────────────────
   Quotation Modal — full BOQ editor
───────────────────────────────────────────── */
function QuotationModal({ quotation, clients, projects, prefilledClient, prefilledProject, onClose, onSaved }) {
  const today = new Date().toISOString().split('T')[0];
  const validDate = new Date(); validDate.setDate(validDate.getDate() + 15);

  const [form, setForm] = useState({
    clientName:  quotation?.clientName || quotation?.client?.name || prefilledClient?.name || '',
    client:      quotation?.client?._id || quotation?.client || prefilledClient?._id || '',
    projectName: quotation?.projectName || quotation?.project?.title || prefilledProject?.title || '',
    project:     quotation?.project?._id || quotation?.project || prefilledProject?._id || '',
    date:        quotation?.date?.split('T')[0] || today,
    validUntil:  quotation?.validUntil?.split('T')[0] || validDate.toISOString().split('T')[0],
    status:      quotation?.status || 'draft',
    gstPercent:     quotation?.gstPercent ?? 18,
    discount:       quotation?.discount ?? 0,
    transportation: quotation?.transportation ?? 0,
    installation:   quotation?.installation ?? 0,
    notes:           quotation?.notes || 'This quotation is valid till the above mentioned date.\n50% Advance, 40% During Execution, 10% After Completion.',
    rejectionReason: quotation?.rejectionReason || '',
  });
  const [sections, setSections] = useState(
    quotation?.sections?.length
      ? quotation.sections.map(s => ({
          title: s.title,
          items: (s.items || []).map(i => ({ description: i.description, heightFt: i.heightFt ?? '', widthFt: i.widthFt ?? '', unit: i.unit || 'Nos', qty: i.qty, rate: i.rate })),
        }))
      : freshSections()
  );
  const [saving, setSaving] = useState(false);

  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));

  // New quotation opened with a pre-selected project (e.g. from Projects page): auto-fill BOQ
  useEffect(() => {
    if (quotation || !prefilledProject?._id) return;
    API.get(`/material-planning/${prefilledProject._id}`).then(r => {
      const plan = r.data;
      if (!plan?.categories?.length) return;
      setSections(plan.categories.map(cat => {
        const items = catToItems(cat);
        return {
          title: cat.title,
          items: items.length ? items : [freshItem()],
        };
      }));
    }).catch(() => {});
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  function addSection() { setSections(p => [...p, { title: 'New Section', items: [freshItem()] }]); }
  function removeSection(si) { setSections(p => p.filter((_, i) => i !== si)); }
  function updateSection(si, k, v) { setSections(p => p.map((s, i) => i === si ? { ...s, [k]: v } : s)); }

  function addItem(si) {
    setSections(p => p.map((s, i) => i !== si ? s : { ...s, items: [...s.items, freshItem()] }));
  }
  function removeItem(si, ii) {
    setSections(p => p.map((s, i) => i !== si ? s : { ...s, items: s.items.filter((_, k) => k !== ii) }));
  }
  function updateItem(si, ii, k, v) {
    setSections(p => p.map((s, i) => i !== si ? s : {
      ...s, items: s.items.map((item, l) => {
        if (l !== ii) return item;
        const updated = { ...item, [k]: v };
        if (k === 'heightFt' || k === 'widthFt') {
          const h = +updated.heightFt, w = +updated.widthFt;
          updated.qty = (h > 0 && w > 0) ? +(h * w).toFixed(2) : '';
        }
        return updated;
      }),
    }));
  }

  function onClientChange(clientId) {
    const found = clients.find(c => c._id === clientId);
    set('client', clientId);
    set('clientName', found ? found.name : '');
    // reset project if it doesn't belong to this client
    const projFound = projects.find(p => p._id === form.project);
    const projClientId = projFound?.client?._id || projFound?.client;
    if (projClientId && projClientId !== clientId) {
      set('project', '');
      set('projectName', '');
    }
  }

  function onProjectChange(projectId) {
    const found = projects.find(p => p._id === projectId);
    set('project', projectId);
    set('projectName', found ? found.title : '');
    // auto-fill client if not already set
    if (!form.client && found?.client) {
      const clientId = found.client?._id || found.client;
      const cl = clients.find(c => c._id === clientId);
      if (cl) { set('client', cl._id); set('clientName', cl.name); }
    }

    // New quotation: auto-fill BOQ from the selected project's Material Plan,
    // replacing whatever sections came from a previously selected project.
    if (!quotation && projectId) {
      API.get(`/material-planning/${projectId}`).then(r => {
        const plan = r.data;
        if (!plan?.categories?.length) {
          setSections([{ title: '', items: [freshItem()] }]);
          return;
        }
        setSections(plan.categories.map(cat => {
          const items = catToItems(cat);
          return {
            title: cat.title,
            items: items.length ? items : [freshItem()],
          };
        }));
      }).catch(() => {});
    }
  }

  const filteredProjects = form.client
    ? projects.filter(p => (p.client?._id || p.client) === form.client)
    : projects;

  const totals = (() => {
    let sub = 0;
    sections.forEach(s => s.items.forEach(it => { sub += (+(it.qty || 0)) * (+(it.rate || 0)); }));
    const transport = +(form.transportation || 0);
    const install   = +(form.installation   || 0);
    const gst       = sub * (+(form.gstPercent) / 100);
    const total     = sub + transport + install + gst - +(form.discount || 0);
    return { sub, transport, install, gst, total };
  })();

  async function save() {
    if (!form.clientName.trim()) return toast.error('Customer name is required');
    setSaving(true);
    try {
      // Drop any blank/unfinished item rows before submitting —
      // the schema requires a description on every item.
      const cleanedSections = sections
        .map(sec => ({ ...sec, items: sec.items.filter(it => it.description?.trim()) }))
        .filter(sec => sec.items.length > 0);
      const payload = { ...form, sections: cleanedSections };
      const prevStatus = quotation?.status;
      const r = quotation
        ? await API.put(`/quotations/${quotation._id}`, payload)
        : await API.post('/quotations', payload);
      onSaved(r.data, prevStatus);
    } catch (e) { toast.error(e.response?.data?.message || 'Save failed'); }
    finally { setSaving(false); }
  }

  return (
    <div className="quo-overlay">
      <div className="quo-modal" onClick={e => e.stopPropagation()}>
        <div className="quo-modal-header">
          <h3>{quotation ? `Edit ${quotation.quotationNumber}` : 'New Quotation'}</h3>
          <button className="quo-modal-close" onClick={onClose}><FiX /></button>
        </div>
        <div className="quo-modal-body">
          <div className="quo-mrow">
            <div className="quo-mgroup">
              <label>Customer *</label>
              <ProjectSearchSelect
                projects={clients.map(c => ({ _id: c._id, title: c.name }))}
                value={form.client}
                objectMode={false}
                onChange={id => onClientChange(id || '')}
                placeholder="Select Customer…"
                className="quo-proj-pss"
              />
            </div>
            <div className="quo-mgroup">
              <label>Project</label>
              <ProjectSearchSelect
                projects={filteredProjects}
                value={form.project}
                objectMode={false}
                onChange={id => onProjectChange(id || '')}
                placeholder={form.client ? (filteredProjects.length ? 'Select Project…' : 'No projects for this customer') : 'Select Customer first…'}
                className="quo-proj-pss"
              />
            </div>
          </div>
          <div className="quo-mrow">
            <div className="quo-mgroup">
              <label>Date</label>
              <input type="date" value={form.date} onChange={e => set('date', e.target.value)} />
            </div>
            <div className="quo-mgroup">
              <label>Valid Until</label>
              <input type="date" value={form.validUntil} onChange={e => set('validUntil', e.target.value)} />
            </div>
            <div className="quo-mgroup">
              <label>Status</label>
              {quotation ? (
                <select value={form.status} onChange={e => set('status', e.target.value)}>
                  {Object.entries(STATUS_CFG).filter(([k]) => k !== 'sent' || form.status === 'sent').map(([k, v]) => <option key={k} value={k}>{v.label}</option>)}
                </select>
              ) : (
                <input value="Draft" disabled style={{ background: '#f8fafc', color: '#64748b', cursor: 'not-allowed' }} />
              )}
            </div>
            {form.status === 'rejected' && (
              <div className="quo-mgroup" style={{ gridColumn: '1 / -1' }}>
                <label>Rejection Reason</label>
                <textarea
                  rows={2}
                  className="quo-field"
                  placeholder="Reason for rejection…"
                  value={form.rejectionReason}
                  onChange={e => set('rejectionReason', e.target.value)}
                  style={{ resize: 'vertical' }}
                />
              </div>
            )}
          </div>

          <div className="quo-boq-editor">
            <div className="quo-boq-editor-header">
              <span>BOQ Sections</span>
              <button className="quo-btn-ghost quo-sm" onClick={addSection}><FiPlus size={11}/> Add Section</button>
            </div>
            {sections.map((sec, si) => (
              <div key={si} className="quo-editor-section">
                <div className="quo-editor-sec-header">
                  <span className="quo-editor-sec-num">{si + 1}</span>
                  <input className="quo-editor-sec-title" value={sec.title} onChange={e => updateSection(si, 'title', e.target.value)} />
                  <button className="quo-sec-remove" onClick={() => removeSection(si)}><FiX size={11}/></button>
                </div>
                <div className="quo-editor-table-wrap">
                <table className="quo-editor-table">
                  <thead><tr><th>Description</th><th>Height (ft)</th><th>Width (ft)</th><th>Total Area (SQ/FT)</th><th>Qty</th><th>Rate (₹)</th><th>Amount</th><th></th></tr></thead>
                  <tbody>
                    {sec.items.map((item, ii) => (
                      <tr key={ii}>
                        <td><input value={item.description} onChange={e => updateItem(si, ii, 'description', e.target.value)} placeholder="Item description" /></td>
                        <td><input type="number" min="0" step="0.01" value={item.heightFt} onChange={e => updateItem(si, ii, 'heightFt', e.target.value)} placeholder="0" /></td>
                        <td><input type="number" min="0" step="0.01" value={item.widthFt} onChange={e => updateItem(si, ii, 'widthFt', e.target.value)} placeholder="0" /></td>
                        <td className="quo-editor-amt">{(item.heightFt && item.widthFt) ? (item.heightFt * item.widthFt).toLocaleString('en-IN') : '–'}</td>
                        <td><input type="number" min="0" value={item.qty} readOnly={!!(item.heightFt && item.widthFt)} title={(item.heightFt && item.widthFt) ? 'Auto-calculated from Height × Width' : undefined} onChange={e => updateItem(si, ii, 'qty', +e.target.value)} /></td>
                        <td><input type="number" min="0" value={item.rate} onChange={e => updateItem(si, ii, 'rate', +e.target.value)} /></td>
                        <td className="quo-editor-amt">{fmtINR((+item.qty || 0) * (+item.rate || 0))}</td>
                        <td><button className="quo-cl-remove" onClick={() => removeItem(si, ii)}><FiX size={10}/></button></td>
                      </tr>
                    ))}
                  </tbody>
                </table>
                </div>
                <button className="quo-add-item-btn" onClick={() => addItem(si)}><FiPlus size={10}/> Add Item</button>
              </div>
            ))}
          </div>

          <div className="quo-mrow quo-totals-row">
            <div className="quo-totals-inputs">
              <div className="quo-mgroup">
                <label>GST %</label>
                <input type="number" min="0" max="100" value={form.gstPercent} onChange={e => set('gstPercent', +e.target.value)} />
              </div>
              <div className="quo-mgroup">
                <label>Transportation (₹)</label>
                <input type="number" min="0" value={form.transportation} onChange={e => set('transportation', +e.target.value)} />
              </div>
              <div className="quo-mgroup">
                <label>Installation (₹)</label>
                <input type="number" min="0" value={form.installation} onChange={e => set('installation', +e.target.value)} />
              </div>
              <div className="quo-mgroup">
                <label>Discount (₹)</label>
                <input type="number" min="0" value={form.discount} onChange={e => set('discount', +e.target.value)} />
              </div>
            </div>
            <div className="quo-totals-preview">
              <div className="quo-totals-line"><span>Sub Total</span><span>{fmtINR(totals.sub)}</span></div>
              {totals.transport > 0 && <div className="quo-totals-line"><span>Transportation</span><span>{fmtINR(totals.transport)}</span></div>}
              {totals.install   > 0 && <div className="quo-totals-line"><span>Installation</span><span>{fmtINR(totals.install)}</span></div>}
              <div className="quo-totals-line"><span>GST ({form.gstPercent}%)</span><span>{fmtINR(totals.gst)}</span></div>
              {+(form.discount || 0) > 0 && <div className="quo-totals-line quo-discount-line"><span>Discount</span><span>-{fmtINR(form.discount)}</span></div>}
              <div className="quo-totals-line quo-totals-grand"><span>Total</span><span>{fmtINR(totals.total)}</span></div>
            </div>
          </div>

          <div className="quo-mgroup">
            <label>Notes (one per line)</label>
            <textarea rows={3} value={form.notes} onChange={e => set('notes', e.target.value)} />
          </div>
        </div>
        <div className="quo-modal-footer">
          <button className="quo-btn-ghost" onClick={onClose}>Cancel</button>
          <button className="quo-btn-primary" onClick={save} disabled={saving}>{saving ? 'Saving…' : quotation ? 'Update Quotation' : 'Create Quotation'}</button>
        </div>
      </div>
    </div>
  );
}
